import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuthStore } from "@/lib/stores/authStore";
import { usePathModeStore } from "@/lib/stores/pathModeStore";
import { useTrainerAuthRedirect } from "@/lib/hooks/useTrainerAuthRedirect";
import { showToast } from "@/lib/utils/toast";
import { isApiError } from "@/lib/api/client";
import registerImage from "@/assets/exercise-background-photos/register.png";
import { AuthFormCard } from "@/components/AuthFormCard";
import { AuthHeader } from "@/components/AuthHeader";
import { AuthBackButton } from "@/components/AuthBackButton";
import { AuthErrorAlert } from "@/components/AuthErrorAlert";
import { AuthTextInput } from "@/components/AuthTextInput";
import { AuthPasswordInput } from "@/components/AuthPasswordInput";
import { AuthFooterLink } from "@/components/AuthFooterLink";
import { NicknameSuggestions } from "@/components/NicknameSuggestions";

export const Route = createFileRoute("/trainer/auth/register")({
  component: TrainerRegister,
});

function TrainerRegister() {
  const navigate = useNavigate();
  const intl = useIntl();
  const [nicknameServerError, setNicknameServerError] = useState<string | null>(
    null,
  );
  const [nicknameSuggestions, setNicknameSuggestions] = useState<string[]>([]);

  const {
    user,
    register: registerUser,
    isLoading,
    error,
    clearError,
  } = useAuthStore();
  const setMode = usePathModeStore((s) => s.setMode);
  const { goAfterAuth } = useTrainerAuthRedirect();

  const registerSchema = z
    .object({
      nickname: z
        .string()
        .min(1, intl.formatMessage({ id: "validation.nickname.required" })),
      gender: z.enum(["male", "female"]).nullable().optional(),
      email: z
        .string()
        .email(intl.formatMessage({ id: "trainer.auth.email.invalid" }))
        .optional()
        .or(z.literal("")),
      password: z
        .string()
        .min(6, intl.formatMessage({ id: "validation.password.minLength" })),
      confirmPassword: z
        .string()
        .min(
          1,
          intl.formatMessage({ id: "validation.confirmPassword.required" }),
        ),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: intl.formatMessage({ id: "validation.confirmPassword.mustMatch" }),
      path: ["confirmPassword"],
    });

  type RegisterFormData = z.infer<typeof registerSchema>;

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({ resolver: zodResolver(registerSchema) });

  useEffect(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (user?.accountType === "TRAINER") {
      goAfterAuth();
    }
  }, [user, goAfterAuth]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      clearError();
      await registerUser({
        nickname: data.nickname,
        password: data.password,
        accountType: "TRAINER",
        ...(data.gender !== undefined ? { gender: data.gender } : {}),
        ...(data.email ? { email: data.email } : {}),
      });
      setMode("TRAINER");
      showToast.success(
        intl.formatMessage(
          { id: "toast.auth.registerSuccess" },
          { nickname: data.nickname },
        ),
      );
      goAfterAuth();
    } catch (err) {
      if (
        isApiError(err) &&
        err.status === 409 &&
        Array.isArray(err.data?.suggestions)
      ) {
        const message = err.message || "register.nickname.taken";
        setNicknameServerError(
          message.startsWith("validation.") || message.startsWith("register.")
            ? intl.formatMessage({ id: message })
            : message,
        );
        setNicknameSuggestions(err.data.suggestions.slice(0, 5));
        clearError();
      }
    }
  };

  return (
    <div className="flex flex-col relative">
      <AuthHeader
        image={registerImage}
        imageAlt="Sign Up"
        title={<FormattedMessage id="register.createAccount" />}
        subtitle={<FormattedMessage id="trainer.auth.registerSubtitle" />}
      />

      <AuthFormCard>
        <div className="flex flex-col">
          <AuthBackButton onClick={() => navigate({ to: "/trainer/auth" })} />

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && !nicknameServerError && (
              <AuthErrorAlert>
                {error.startsWith("validation.") ||
                error.startsWith("register.")
                  ? intl.formatMessage({ id: error })
                  : error}
              </AuthErrorAlert>
            )}

            <AuthTextInput
              {...register("nickname", {
                onChange: () => {
                  clearError();
                  if (nicknameServerError) setNicknameServerError(null);
                  if (nicknameSuggestions.length) setNicknameSuggestions([]);
                },
              })}
              type="text"
              placeholder={intl.formatMessage({
                id: "register.nickname.placeholder",
              })}
              invalid={!!nicknameServerError}
              disabled={isLoading}
              error={errors.nickname?.message}
            >
              <NicknameSuggestions
                serverError={nicknameServerError}
                suggestions={nicknameSuggestions}
                onSelect={(s) => {
                  setValue("nickname", s, { shouldValidate: true });
                  setNicknameServerError(null);
                  setNicknameSuggestions([]);
                }}
              />
            </AuthTextInput>

            <AuthTextInput
              {...register("email")}
              type="email"
              placeholder={intl.formatMessage({
                id: "trainer.auth.email.placeholder",
              })}
              disabled={isLoading}
              error={errors.email?.message}
            >
              <p className="text-micro text-gray px-1">
                <FormattedMessage id="trainer.auth.email.hint" />
              </p>
            </AuthTextInput>

            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {["male", "female"].map((gender) => (
                      <button
                        key={gender}
                        type="button"
                        onClick={() => {
                          clearError();
                          field.onChange(gender);
                        }}
                        className={`h-13 rounded-xl text-small transition-all duration-200 ${
                          field.value === gender
                            ? "bg-primary text-white"
                            : "bg-secondary text-dark-gray "
                        }`}
                        disabled={isLoading}
                      >
                        {gender === "male" && (
                          <FormattedMessage id="register.gender.male" />
                        )}
                        {gender === "female" && (
                          <FormattedMessage id="register.gender.female" />
                        )}
                      </button>
                    ))}
                  </div>
                  <button
                    key="prefer-not-to-say"
                    type="button"
                    onClick={() => {
                      clearError();
                      field.onChange(null);
                    }}
                    className={`h-13 w-full rounded-xl text-small transition-all duration-200 ${
                      field.value === null
                        ? "bg-primary text-white"
                        : "bg-secondary text-dark-gray "
                    }`}
                    disabled={isLoading}
                    data-testid="register-gender-unspecified"
                  >
                    <FormattedMessage id="register.gender.preferNotToSay" />
                  </button>
                  {errors.gender && (
                    <p className="text-dark text-small font-medium">
                      {errors.gender.message}
                    </p>
                  )}
                </div>
              )}
            />

            <AuthPasswordInput
              {...register("password")}
              placeholder={intl.formatMessage({
                id: "register.password.placeholder",
              })}
              disabled={isLoading}
              error={errors.password?.message}
            />

            <AuthPasswordInput
              {...register("confirmPassword")}
              placeholder={intl.formatMessage({
                id: "register.confirmPassword.placeholder",
              })}
              disabled={isLoading}
              error={errors.confirmPassword?.message}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <FormattedMessage id="register.creating" />
              ) : (
                <FormattedMessage id="register.createAccount" />
              )}
            </Button>

            <AuthFooterLink
              promptId="register.alreadyHaveAccount"
              linkId="register.signIn"
              to="/trainer/auth/login"
            />
          </form>
        </div>
      </AuthFormCard>
    </div>
  );
}
